import { type DefaultMantineColor, Group, Stack, Text } from '@mantine/core';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

type Mode = 'date' | 'dateTime';
type ValueRepresents = 'utcInstant' | 'wallClockDate';
type TimeZoneStyle = 'stack' | 'inline';
type TimestampStyle = 'long' | 'shortFixed' | 'shortEstimate';

type DateTimeCellProps = {
  value: string | null;
  valueRepresents?: ValueRepresents;
  mode?: Mode;
  sourceTimeZone?: string | null;
  timeZoneStyle?: TimeZoneStyle;
  timeStampStyle?: TimestampStyle;
  emptyLabel?: string;
  prefix?: string;
  relativeDisplayPolicy?: (context: {
    days: number;
    isPast: boolean;
    isToday: boolean;
    isHovered: boolean;
    isFocused: boolean;
  }) => {
    visible: boolean;
    color?: DefaultMantineColor | 'dimmed';
  };
};

const timeModeAtom = atomWithStorage('date-time-cell:view-mode', 'source');

export default function DateTimeCell({
  value,
  valueRepresents = 'utcInstant',
  mode = 'dateTime',
  sourceTimeZone,
  timeZoneStyle = 'stack',
  timeStampStyle = 'long',
  emptyLabel = 'Unscheduled',
  prefix,
  relativeDisplayPolicy,
}: DateTimeCellProps) {
  const [timeMode, setTimeMode] = useAtom(timeModeAtom);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const resolvedSourceTimeZone = getResolvedTimeZone(sourceTimeZone ?? null);
  const viewerTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // If the value represents a wall clock date, we do not allow switching between local and viewer timezone
  const activeTimeZone =
    valueRepresents === 'wallClockDate'
      ? resolvedSourceTimeZone
      : timeMode === 'viewer'
        ? getResolvedTimeZone(viewerTimeZone)
        : resolvedSourceTimeZone;

  const now = useMemo(
    () => DateTime.local({ zone: activeTimeZone ?? undefined }).startOf('day'),
    [activeTimeZone],
  );
  const target = useMemo(() => {
    if (value === null) return null;
    let t = DateTime.fromISO(
      valueRepresents === 'utcInstant' ? value : value.slice(0, 10),
      {
        zone: activeTimeZone ?? undefined,
      },
    );
    if (valueRepresents === 'wallClockDate') t = t.startOf('day');
    if (!t.isValid) return null;
    return t;
  }, [value, valueRepresents, activeTimeZone]);

  const relativeInfo = useMemo(() => {
    if (!value || !relativeDisplayPolicy) return null;
    if (target === null) return null;

    const days = Math.round(target.diff(now, 'days').days);
    const isToday = days === 0;
    const isPast = days < 0;
    const text = isToday
      ? 'today'
      : days > 0
        ? `+${days}d`
        : `-${Math.abs(days)}d`;

    const policy = relativeDisplayPolicy({
      days,
      isPast,
      isToday,
      isHovered,
      isFocused,
    });

    return {
      text,
      ...policy,
    };
  }, [isFocused, isHovered, now, relativeDisplayPolicy, target, value]);

  const timeZoneLabel =
    valueRepresents === 'wallClockDate'
      ? (resolvedSourceTimeZone ?? 'Source local time')
      : timeMode === 'viewer'
        ? viewerTimeZone
        : (resolvedSourceTimeZone ?? sourceTimeZone ?? 'Source local time');

  if (!value) {
    return (
      <Text size="sm" c="dimmed" fs="italic">
        {emptyLabel}
      </Text>
    );
  }

  return (
    <Stack
      gap={2}
      justify="center"
      style={{ flex: 1, h: '100%', lineHeight: 1.15, cursor: 'pointer' }}
      role="button"
      tabIndex={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onClick={() => {
        if (valueRepresents === 'wallClockDate') return;
        setTimeMode((current) => (current === 'source' ? 'viewer' : 'source'));
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          if (valueRepresents === 'wallClockDate') return;
          event.preventDefault();
          setTimeMode((current) =>
            current === 'source' ? 'viewer' : 'source',
          );
        }
      }}
    >
      <Group gap={6}>
        {prefix && (
          <Text size="sm" fw={600}>
            {prefix}
            {': '}
          </Text>
        )}
        <Text size="sm" fw={500} lh={1.15}>
          {formatDateTimeSummary(
            target?.toISO() ?? null,
            mode,
            activeTimeZone,
            timeZoneStyle,
            timeStampStyle,
            emptyLabel,
          )}
          {relativeInfo ? (
            <Text
              span
              size="sm"
              fw={500}
              c={
                (relativeInfo.color ?? 'dimmed') === 'dimmed'
                  ? 'dimmed'
                  : `${relativeInfo.color}.7`
              }
              style={{
                visibility: relativeInfo.visible ? 'visible' : 'hidden',
              }}
            >
              {' · '}
              {relativeInfo.text}
            </Text>
          ) : null}
        </Text>
      </Group>
      {timeZoneStyle === 'stack' && (
        <Text size="xs" c="dimmed" lh={1.15}>
          {timeZoneLabel}
        </Text>
      )}
    </Stack>
  );
}

function getResolvedTimeZone(timeZone: string | null) {
  if (!timeZone) {
    return null;
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    return timeZone;
  } catch {
    return null;
  }
}

function formatInTimeZone(
  value: string,
  timeZone: string | null,
  options: Intl.DateTimeFormatOptions,
) {
  return new Intl.DateTimeFormat('en-US', {
    ...options,
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(value));
}

function formatDateTimeSummary(
  value: string | null,
  mode: Mode,
  timeZone: string | null,
  timeZoneStyle: TimeZoneStyle,
  timeStampStyle: TimestampStyle,
  emptyLabel: string,
) {
  if (!value) {
    return emptyLabel;
  }

  const resolvedTimeZone = getResolvedTimeZone(timeZone);
  const dateLabel = formatInTimeZone(value, resolvedTimeZone, {
    month: 'short',
    day: 'numeric',
    year: timeStampStyle === 'long' ? 'numeric' : undefined,
    timeZoneName:
      timeZoneStyle === 'inline' && mode === 'date'
        ? 'shortGeneric'
        : undefined,
  });

  if (mode === 'dateTime') {
    const timeLabel = formatInTimeZone(value, resolvedTimeZone, {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName:
        timeZoneStyle === 'inline' && mode === 'dateTime'
          ? 'shortGeneric'
          : undefined,
    });

    if (timeStampStyle === 'long') {
      return `${dateLabel}, ${timeLabel}`;
    } else {
      return `${dateLabel}${timeStampStyle === 'shortFixed' ? ' - ' : ' - By '}${timeLabel}`;
    }
  } else {
    return dateLabel;
  }
}
